// Voxelises Minecraft build shapes into a block grid. Pure — no React, no DOM.
//
// Every shape is produced as a stack of horizontal layers, `layers[y][z][x]`
// (bottom to top). Flat shapes (circle/ellipse) are a single layer. A cell is
// filled when its CENTER is inside the shape — center sampling is what makes a
// blocky circle read as round rather than lopsided.

export type ShapeKind =
  | 'circle' | 'ellipse' | 'sphere' | 'dome'
  | 'cylinder' | 'pyramid' | 'cube' | 'octahedron';

export type FillMode = 'fill' | 'thin' | 'thick';

export interface ShapeConfig {
  kind: ShapeKind;
  /** Primary size / diameter (x, and z for round shapes). */
  width: number;
  /** Second dimension: the z-axis for an ellipse, the vertical height for a
   *  cylinder/cube. Ignored (derived) for the others. */
  height: number;
  fill: FillMode;
}

/** A stack of horizontal layers, bottom to top. `layers[y][z][x]`. */
export type Layers = boolean[][][];

export const MIN_SIZE = 1;
export const MAX_SIZE = 128;

export const clampSize = (n: number) =>
  Math.max(MIN_SIZE, Math.min(MAX_SIZE, Math.round(n) || MIN_SIZE));

// UI metadata: which shapes take a second (height) input, and what to call it.
export interface ShapeMeta {
  id: ShapeKind;
  label: string;
  /** Whether the second size input is shown. */
  usesHeight: boolean;
  heightLabel: string;
  /** Whether the shape has more than one layer (→ show the layer slider). */
  is3D: boolean;
}

export const SHAPES: ShapeMeta[] = [
  { id: 'circle', label: 'Circle', usesHeight: false, heightLabel: '', is3D: false },
  { id: 'ellipse', label: 'Ellipse', usesHeight: true, heightLabel: 'Depth', is3D: false },
  { id: 'sphere', label: 'Sphere', usesHeight: false, heightLabel: '', is3D: true },
  { id: 'dome', label: 'Dome', usesHeight: false, heightLabel: '', is3D: true },
  { id: 'cylinder', label: 'Cylinder', usesHeight: true, heightLabel: 'Height', is3D: true },
  { id: 'pyramid', label: 'Pyramid', usesHeight: false, heightLabel: '', is3D: true },
  { id: 'cube', label: 'Cube', usesHeight: true, heightLabel: 'Height', is3D: true },
  { id: 'octahedron', label: 'Diamond', usesHeight: false, heightLabel: '', is3D: true },
];

export const shapeMeta = (kind: ShapeKind) => SHAPES.find(s => s.id === kind) as ShapeMeta;

// --------------------------------------------------------------- predicates ---

// Center-of-cell inside test for an axis-aligned ellipse of size w x d.
function insideEllipse(x: number, z: number, w: number, d: number): boolean {
  const rx = w / 2, rz = d / 2;
  const nx = (x + 0.5 - rx) / rx;
  const nz = (z + 0.5 - rz) / rz;
  return nx * nx + nz * nz <= 1;
}

interface Dims { w: number; d: number; h: number } // x, z, y extents

function dimsFor(kind: ShapeKind, width: number, height: number): Dims {
  const w = clampSize(width);
  switch (kind) {
    case 'circle': return { w, d: w, h: 1 };
    case 'ellipse': return { w, d: clampSize(height), h: 1 };
    case 'sphere': return { w, d: w, h: w };
    case 'octahedron': return { w, d: w, h: w };
    case 'cylinder': return { w, d: w, h: clampSize(height) };
    case 'cube': return { w, d: w, h: clampSize(height) };
    case 'pyramid': return { w, d: w, h: Math.ceil(w / 2) };
    case 'dome': return { w, d: w, h: w }; // built as a full sphere, then sliced
  }
}

function predicate(kind: ShapeKind, dims: Dims): (x: number, y: number, z: number) => boolean {
  const { w, d } = dims;
  switch (kind) {
    case 'circle':
    case 'ellipse':
    case 'cylinder':
      return (x, _y, z) => insideEllipse(x, z, w, d);
    case 'cube':
      return () => true;
    case 'pyramid':
      // Each layer up insets by one on every side (a stepped pyramid).
      return (x, y, z) => Math.min(x, w - 1 - x, z, d - 1 - z) >= y;
    case 'sphere':
    case 'dome': {
      const r = w / 2;
      return (x, y, z) => {
        const nx = (x + 0.5 - r) / r, ny = (y + 0.5 - r) / r, nz = (z + 0.5 - r) / r;
        return nx * nx + ny * ny + nz * nz <= 1;
      };
    }
    case 'octahedron': {
      const c = w / 2, r = w / 2;
      return (x, y, z) =>
        Math.abs(x + 0.5 - c) + Math.abs(y + 0.5 - c) + Math.abs(z + 0.5 - c) <= r;
    }
  }
}

function buildSolid(dims: Dims, inside: (x: number, y: number, z: number) => boolean): Layers {
  const layers: Layers = [];
  for (let y = 0; y < dims.h; y++) {
    const layer: boolean[][] = [];
    for (let z = 0; z < dims.d; z++) {
      const row: boolean[] = [];
      for (let x = 0; x < dims.w; x++) row.push(inside(x, y, z));
      layer.push(row);
    }
    layers.push(layer);
  }
  return layers;
}

// ------------------------------------------------------------------- shells ---

// A shape is "outlined" by keeping only cells adjacent to an empty cell (or the
// edge). More neighbour directions -> more cells qualify -> a thicker ring, so
// `thick` also checks the diagonals.
const ORTHO_2D = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const DIAG_2D = [[1, 1], [1, -1], [-1, 1], [-1, -1]];

function shell2D(layer: boolean[][], mode: FillMode): boolean[][] {
  const d = layer.length, w = layer[0]?.length ?? 0;
  const dirs = mode === 'thick' ? ORTHO_2D.concat(DIAG_2D) : ORTHO_2D;
  const out: boolean[][] = [];
  for (let z = 0; z < d; z++) {
    const row = layer[z];
    const outRow = new Array<boolean>(w);
    for (let x = 0; x < w; x++) {
      let edge = false;
      if (row[x]) {
        for (let i = 0; i < dirs.length && !edge; i++) {
          const nx = x + dirs[i][0], nz = z + dirs[i][1];
          edge = nz < 0 || nz >= d || nx < 0 || nx >= w || !layer[nz][nx];
        }
      }
      outRow[x] = edge;
    }
    out.push(outRow);
  }
  return out;
}

const FACE_3D = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
const EDGE_3D = [
  [1, 1, 0], [1, -1, 0], [-1, 1, 0], [-1, -1, 0],
  [0, 1, 1], [0, 1, -1], [0, -1, 1], [0, -1, -1],
  [1, 0, 1], [1, 0, -1], [-1, 0, 1], [-1, 0, -1],
];

/**
 * A layer stack copied into one flat array with a one-cell empty border, so a neighbour test is
 * a single index lookup with no bounds checks. A 128-wide shell test runs a few million of
 * these; on the nested arrays with a closure per lookup it took 100-400 ms and froze the slider.
 */
function padded(layers: Layers) {
  const h = layers.length, d = layers[0]?.length ?? 0, w = layers[0]?.[0]?.length ?? 0;
  const W = w + 2, D = d + 2, H = h + 2;
  const grid = new Uint8Array(W * D * H);
  for (let y = 0; y < h; y++) for (let z = 0; z < d; z++) {
    const row = layers[y][z];
    const base = ((y + 1) * D + (z + 1)) * W + 1;
    for (let x = 0; x < w; x++) if (row[x]) grid[base + x] = 1;
  }
  const offsets = (dirs: number[][]) => dirs.map(([dx, dy, dz]) => (dy * D + dz) * W + dx);
  const index = (x: number, y: number, z: number) => ((y + 1) * D + (z + 1)) * W + x + 1;
  return { grid, h, d, w, offsets, index };
}

function shell3D(layers: Layers, mode: FillMode): Layers {
  const { grid, h, d, w, offsets, index } = padded(layers);
  const offs = offsets(mode === 'thick' ? FACE_3D.concat(EDGE_3D) : FACE_3D);
  const out: Layers = [];
  for (let y = 0; y < h; y++) {
    const layer: boolean[][] = [];
    for (let z = 0; z < d; z++) {
      const row = new Array<boolean>(w);
      const base = index(0, y, z);
      for (let x = 0; x < w; x++) {
        const i = base + x;
        let edge = false;
        if (grid[i]) for (let k = 0; k < offs.length && !edge; k++) edge = grid[i + offs[k]] === 0;
        row[x] = edge;
      }
      layer.push(row);
    }
    out.push(layer);
  }
  return out;
}

// Round/stacked-profile shapes outline as a ring at each height (walls / tube);
// closed solids outline as a full 3-D surface shell.
const PER_LAYER_SHELL: ShapeKind[] = ['circle', 'ellipse', 'cylinder', 'pyramid'];

// ------------------------------------------------------------- public build ---

export function buildShape(config: ShapeConfig): Layers {
  const { kind, fill } = config;

  // A dome is the top half of a sphere (widest layer on the ground). Building
  // the full sphere first means a hollow dome is automatically open at the base.
  // Slice at floor(h/2): for an even diameter the two tied-widest layers belong
  // to different halves, so this keeps exactly one (no duplicated base band).
  if (kind === 'dome') {
    const dims = dimsFor('sphere', config.width, config.height);
    let sphere = buildSolid(dims, predicate('sphere', dims));
    if (fill !== 'fill') sphere = shell3D(sphere, fill);
    return sphere.slice(Math.floor(dims.h / 2));
  }

  // A diamond is a true octahedron built as a stacked diamond (double pyramid):
  // the L1 radius shrinks by one per layer to a single-block point at the top
  // and bottom. Built on integer cell indices so it ALWAYS tapers to a point —
  // the continuous |x+0.5-c| form left even sizes with empty end layers.
  if (kind === 'octahedron') {
    const n = builtWidth('octahedron', config.width); // odd — a point-tipped diamond can't be even
    const rMax = (n - 1) / 2;
    const inside = (x: number, y: number, z: number) =>
      Math.abs(x - rMax) + Math.abs(y - rMax) + Math.abs(z - rMax) <= rMax;
    let layers = buildSolid({ w: n, d: n, h: n }, inside);
    if (fill !== 'fill') layers = shell3D(layers, fill);
    return layers;
  }

  const dims = dimsFor(kind, config.width, config.height);

  // Every layer of a cylinder is the same circle or ring: work it out once and stack it.
  // (Layers are read-only everywhere, so sharing the one row array is safe.)
  if (kind === 'cylinder') {
    let ring = buildSolid({ ...dims, h: 1 }, predicate(kind, dims))[0];
    if (fill !== 'fill') ring = shell2D(ring, fill);
    return Array.from({ length: dims.h }, () => ring);
  }

  let layers = buildSolid(dims, predicate(kind, dims));
  if (fill === 'fill') return layers;

  if (PER_LAYER_SHELL.includes(kind)) {
    layers = layers.map(layer => shell2D(layer, fill));
  } else {
    layers = shell3D(layers, fill);
  }
  return layers;
}

// -------------------------------------------------------------------- counts ---

export const countGrid = (grid: boolean[][]) =>
  grid.reduce((s, row) => s + row.reduce((a, c) => a + (c ? 1 : 0), 0), 0);

export const countLayers = (layers: Layers) =>
  layers.reduce((s, layer) => s + countGrid(layer), 0);

/** Middle layer index of a stack of `n` layers (widest, for a sphere). */
export const equatorIndex = (n: number) => Math.floor((n - 1) / 2);

/**
 * The layer the preview opens on. Picked by shape, not by block count: on a hollow sphere the
 * nearly flat caps hold MORE blocks than the equator ring, so "the layer with the most blocks"
 * opened a 16-wide sphere on layer 2. Shapes that are widest in the middle open there; shapes
 * built up from the ground open on the layer you place first.
 */
export function defaultLayerIndex(kind: ShapeKind, layerCount: number): number {
  return kind === 'sphere' || kind === 'octahedron' ? equatorIndex(layerCount) : 0;
}

/**
 * Whether "thick outline" differs from "outline". Thick fills the diagonal steps of a curve or
 * slope; a cube's or a pyramid's layers are plain squares with no diagonal steps, so for them
 * the two are identical at every size.
 */
export const hasThickOutline = (kind: ShapeKind) => kind !== 'cube' && kind !== 'pyramid';

/**
 * The width the shape is actually built at. A diamond tapers to a single-block point at both
 * ends, which only an odd width allows, so an even size builds one smaller.
 */
export const builtWidth = (kind: ShapeKind, width: number) => {
  const w = clampSize(width);
  return kind === 'octahedron' ? 2 * Math.floor((w - 1) / 2) + 1 : w;
};

// ------------------------------------------------------------- 3D surface ---

/**
 * The filled cells that sit on the OUTER surface — a cell that is filled and
 * has at least one empty face-neighbour (or is on the bounding box). The 3D
 * preview renders only these: interior cubes are never visible, so culling them
 * keeps a solid 128-cube shape at ~O(surface area) instead of ~2 million cubes.
 */
export function surfaceCells(layers: Layers): { x: number; y: number; z: number }[] {
  const { grid, h, d, w, offsets, index } = padded(layers);
  const offs = offsets(FACE_3D);
  const out: { x: number; y: number; z: number }[] = [];
  for (let y = 0; y < h; y++) {
    for (let z = 0; z < d; z++) {
      const base = index(0, y, z);
      for (let x = 0; x < w; x++) {
        const i = base + x;
        if (!grid[i]) continue;
        for (let k = 0; k < offs.length; k++) {
          if (grid[i + offs[k]] === 0) { out.push({ x, y, z }); break; }
        }
      }
    }
  }
  return out;
}

/** Bounding dimensions of a layer stack, for centering the 3D scene. */
export const layerBounds = (layers: Layers) => ({
  w: layers[0]?.[0]?.length ?? 0,
  d: layers[0]?.length ?? 0,
  h: layers.length,
});
